import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import cn from 'classnames'

import styles from './styles.module.scss'

import { showAssetForm, showMaintenance, showPrintLabel } from '../../../store/asset/actions'
import { getCompanyAssets, removeAsset } from '../../../api'
import { formatLatestScan } from '../utils'

import { PlusIcon, ThreeDotsIcon, PrintIcon, LocationIcon, MaintenanceIcon } from '../Icons'
import AssetFilters from './AssetFilters'
import Table from '../../Invoices/Table'
import Checkbox from '../../Core/Checkbox/Checkbox2'
import AssetStatus from '../AssetStatus'

const columnHelper = createColumnHelper()

const Assets = ({companyId}) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const { t } = useTranslation()

  const [openActionMenuId, setOpenActionMenuId] = useState(null)
  const [actionMenuPosition, setActionMenuPosition] = useState({top: 0, left: 0})
  const [assets, setAssets] = useState([])
  const [filters, setFilters] = useState({
    category: 'all',
    ownership: 'all',
    status: 'all',
    assigned: 'all',
    place: 'all',
    supplier: 'all',
    maintenance: 'all',
    scanAge: 'all',
  })

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (e.target instanceof Element && (e.target.closest('[data-action-menu]') || e.target.closest('[data-action-menu-popover]'))) {
        return
      }
      setOpenActionMenuId(null)
    }

    const handleWindowResize = () => {
      setOpenActionMenuId(null)
    }

    document.addEventListener('click', handleDocumentClick)
    window.addEventListener('resize', handleWindowResize)
    window.addEventListener('scroll', handleWindowResize, true)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
      window.removeEventListener('resize', handleWindowResize)
      window.removeEventListener('scroll', handleWindowResize, true)
    }
  }, [])

  useEffect(() => {
    const handler = (event) => {
      getAssets()
    }
    window.addEventListener('asset-updated', handler)
    return () => {
      window.removeEventListener('asset-updated', handler)
    }
  }, [])

  useEffect(() => {
    getAssets()
  }, [companyId])

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    }),
    columnHelper.display({
      id: 'asset',
      header: t('Asset'),
      cell: ({ row }) => {
        return (
          <div className={styles.assetCell}>
            <div className={styles.assetImageWrapper}>
              <img src={row.original.details.photo} className={styles.assetImage} />
            </div>
            <div>
              <div className={styles.assetName}>{row.original.name}</div>
              <div className={styles.assetCategory}>{row.original.code} · {row.original.category.name}</div>
            </div>
          </div>
        )
      }
    }),
    columnHelper.accessor('status', {
      enableSorting: false,
      header: t('Status'),
      cell: ({getValue}) => <AssetStatus status={getValue()} />
    }),
    columnHelper.accessor(row => row.employee ? `${row.employee.name} ${row.employee.surname}` : t('Unassigned'), {
      id: 'assigned',
      enableSorting: false,
      header: t('Assigned to'),
    }),
    columnHelper.accessor('category.name', {
      enableSorting: false,
      header: t('Category'),
    }),
    columnHelper.accessor('ownership.name', {
      enableSorting: false,
      header: t('Ownership'),
    }),
    columnHelper.accessor('place.name', {
      enableSorting: false,
      header: t('Project'),
    }),
    columnHelper.accessor('location', {
      enableSorting: false,
      header: t('Location'),
    }),
    columnHelper.accessor('latest_scan', {
      enableSorting: false,
      header: t('Last scan'),
      cell: ({ getValue }) => {
        const lastScan = getValue()
        return formatLatestScan(lastScan)
      }
    }),
    columnHelper.display({
      id: 'actions',
      enableSorting: false,
      header: '',
      cell: ({ row }) => (
        <div className={styles.actionMenuWrapper} data-action-menu>
          <button
            type='button'
            className={styles.actionButton}
            onClick={e => handleToggleActionMenu(e, row.original.id)}
          >
            <ThreeDotsIcon />
          </button>
        </div>
      ),
    })
  ], [t])

  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      if (filters.search) {
        const search = filters.search.toLowerCase()
        if (!asset.name.toLowerCase().includes(search)) {
          return false
        }
      }
      if (filters.category !== 'all' && asset.category.id !== filters.category) {
        return false
      }
      if (filters.ownership !== 'all' && asset.ownership.id !== filters.ownership) {
        return false
      }
      if (filters.status !== 'all' && asset.status !== filters.status) {
        return false
      }
      if (filters.assigned === 'assigned' && !asset.employee) {
        return false
      }
      if (filters.assigned === 'unassigned' && asset.employee) {
        return false
      }
      if (!isNaN(Number(filters.assigned))) {
        return asset.employee && asset.employee.id === filters.assigned
      }
      if (filters.place !== 'all' && (!asset.place || asset.place.id !== filters.place)) {
        return false
      }
      if (filters.supplier !== 'all' && (!asset.supplier || asset.supplier !== filters.supplier)) {
        return false
      }
      if (filters.maintenance === 'due' && !asset.maintenance_due) {
        return false
      }
      if (filters.maintenance === 'not_due' && asset.maintenance_due) {
        return false
      }
      if (filters.scanAge === 'never' && asset.latest_scan) {
        return false
      }
      if (filters.scanAge === 'recently') {
        if (!asset.latest_scan) {
          return false
        }
        const scanDate = moment(asset.latest_scan.created_at)
        const now = moment()
        const daysDiff = now.diff(scanDate, 'days')
        if (daysDiff > 7) {
          return false
        }
      }
      if (/^(\d+)_days$/.exec(filters.scanAge)) {
        if (!asset.latest_scan) {
          return false          
        }
        const scanDate = moment(asset.latest_scan.created_at)
        const now = moment()
        const daysDiff = now.diff(scanDate, 'days')
        const match = /^(\d+)_days$/.exec(filters.scanAge)
        const days = Number(match[1])
        if (daysDiff > days) {
          return true
        }
        return false
      }
      return true
    })
  }, [assets, filters])

  const table = useReactTable({
    data: filteredAssets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => String(row.id),
  })

  const getAssets = async () => {
    const query = {
      last_scan: true,
      ownership: true,
      place: true,
      employee: true,
      category: true,
    }
    const res = await getCompanyAssets(companyId, query)
    if (Array.isArray(res)) {
      setAssets(res)
    }
  }

  const handleCreateAsset = () => {
    dispatch(showAssetForm({}))
  }

  const handleEditAsset = (assetId) => {
    const asset = assets.find(a => a.id === assetId)
    if (asset) {
      dispatch(showAssetForm(asset))
    }
  }

  const handleRemoveAsset = async (assetId) => {
    const confirmed = window.confirm(t('Are you sure you want to remove this asset?'))
    if (!confirmed) {
      return
    }
    try {
      await removeAsset(companyId, assetId)
      setAssets(prev => prev.filter(a => a.id !== assetId))
    } catch (error) {
      console.error('Error removing asset:', error)
      alert(t('Failed to remove asset. Please try again.'))
    }
  }

  const handleToggleActionMenu = (e, invoiceId) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setActionMenuPosition({
      top: rect.bottom + 6,
      left: rect.right - 140,
    })
    setOpenActionMenuId(prev => (prev === invoiceId ? null : invoiceId))
  }

  const handleMenuAction = (e, action) => {
    e.stopPropagation()
    setOpenActionMenuId(null)
    action()
  }

  const handleOpenAsset = (assetId) => {
    history.push({search: `?tab=${assetId}`})
  }

  const handleRecordMaintenance = (assetId) => {
    dispatch(showMaintenance({asset_id: assetId}))
  }

  const handleViewLocation = (assetId) => {
    history.push({search: `?tab=locations&asset_id=${assetId}`})
  }

  const handlePrintLabel = (assetId) => {
    dispatch(showPrintLabel({id: assetId}))
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.headerTitle}>{t('Assets')}</div>
          <div className={styles.headerTotals}>{t('{{shown}} shown · {{total}} total', {shown: filteredAssets.length, total: assets.length})}</div>
        </div>
        <button type="button" className={styles.button} onClick={handleCreateAsset}>
          <PlusIcon />
          { t('New Asset') }
        </button>
      </div>
      <div className={styles.filters}>
        <AssetFilters
          companyId={companyId}
          filters={filters}
          sections={{
            search: true,
            category: true,
            ownership: true,
            status: true,
            assigned: true,
            place: true,
            supplier: true,
            maintenance: true,
            scanAge: true,
          }}
          onChange={val => setFilters(prev => ({...prev, ...val}))} />
      </div>
      <Table table={table} columns={columns} />
      {openActionMenuId && (
        <div
          className={styles.actionMenu}
          data-action-menu-popover
          style={{ top: actionMenuPosition.top, left: actionMenuPosition.left }}
        >
          <button
            type='button'
            className={styles.actionMenuItem}
            onClick={e => handleMenuAction(e, () => handleOpenAsset(openActionMenuId))}
          >
            {t('Open asset')}
          </button>
          <button
            type='button'
            className={styles.actionMenuItem}
            onClick={e => handleMenuAction(e, () => handleEditAsset(openActionMenuId))}
          >
            {t('Edit asset')}
          </button>
          <div className={styles.actionMenuDivider} />
          <button
            type='button'
            className={styles.actionMenuItem}
            onClick={e => handleMenuAction(e, () => handleRecordMaintenance(openActionMenuId))}
          >
            <MaintenanceIcon width={16} height={16} />
            {t('Record maintenance')}
          </button>
          <button
            type='button'
            className={styles.actionMenuItem}
            onClick={e => handleMenuAction(e, () => handleViewLocation(openActionMenuId))}
          >
            <LocationIcon width={16} height={16} />
            {t('View location')}
          </button>
          <button
            type='button'
            className={styles.actionMenuItem}
            onClick={e => handleMenuAction(e, () => handlePrintLabel(openActionMenuId))}
          >
            <PrintIcon width={16} height={16} />
            {t('Print label')}
          </button>
          <div className={styles.actionMenuDivider} />
          <button
            type='button'
            className={cn(styles.actionMenuItem, styles.danger)}
            onClick={e => handleMenuAction(e, () => handleRemoveAsset(openActionMenuId))}
          >
            {t('Remove asset')}
          </button>
        </div>
      )}
    </div>
  )
}

export default Assets
